import { createActionGroup, createFeature, createReducer, emptyProps, on, props } from '@ngrx/store';
import { produce } from 'immer';
import { ScreenSize } from '../common/tw-theme';

export interface UiState {
  screenSize: ScreenSize;
  isTouchDevice: boolean;
  sidebar: {
    visible: boolean;
    expanded: boolean;
  };
  navigating: boolean;
  showSplashScreen: boolean;
  minimalUi: boolean;
  routeClass: string | null;
}

export const uiActions = createActionGroup({
  source: 'ui',
  events: {
    'update screen size': props<{ payload: { size: ScreenSize; }; }>(),
    'update touch device': emptyProps(),
    'update navigation state': props<{ navigating: boolean; }>(),
    'hide splash screen': emptyProps(),
    'update minimal ui': props<{ payload: { minimal: boolean; }; }>(),
    'update route class': props<{ payload: { class: 'string'; }; }>()
  }
});

export const sidebarActions = createActionGroup({
  source: 'ui-sidebar',
  events: {
    'toggle visibility': emptyProps(),
    'toggle expansion': emptyProps(),
    'show': emptyProps(),
    'hide': emptyProps(),
    'expand': emptyProps(),
    'contract': emptyProps()
  }
});

export const uiFeature = createFeature({
  name: 'ui',
  reducer: createReducer<UiState>(
    {
      screenSize: 'sm',
      isTouchDevice: false,
      sidebar: {
        visible: false,
        expanded: false,
      },
      navigating: true,
      showSplashScreen: true,
      minimalUi: false,
      routeClass: null
    },
    on(uiActions.updateNavigationState, produce((state, { navigating }) => {
      state.navigating = navigating;
    })),
    on(uiActions.updateScreenSize, produce((state, { payload }) => {
      state.screenSize = payload.size;
      return state;
    })),
    on(uiActions.updateTouchDevice, produce((state) => {
      state.isTouchDevice = isTouchDevice();
      return state;
    })),

    on(sidebarActions.expand, produce(state => {
      state.sidebar.expanded = true;
    })),
    on(sidebarActions.contract, produce(state => {
      state.sidebar.expanded = false;
    })),
    on(sidebarActions.toggleExpansion, produce(state => {
      state.sidebar.expanded = !state.sidebar.expanded;
    })),
    on(sidebarActions.show, produce(state => {
      state.sidebar.visible = true;
    })),
    on(sidebarActions.hide, produce(state => {
      state.sidebar.visible = false;
    })),
    on(sidebarActions.toggleVisibility, produce(state => {
      state.sidebar.visible = !state.sidebar.visible;
    })),

    on(uiActions.hideSplashScreen, produce((state) => {
      state.showSplashScreen = false;
    })),

    on(uiActions.updateMinimalUi, produce((state, { payload }) => {
      state.minimalUi = payload.minimal;
    })),

    on(uiActions.updateRouteClass, produce((state, { payload }) => {
      state.routeClass = payload.class;
    }))
  ),
});

function isTouchDevice() {
  return (('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0));
}